import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Rocket, Sparkles, Loader2, Import } from "lucide-react";
import { AIGenerationLoader } from "@/components/dashboard/AIGenerationLoader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useCredits } from "@/hooks/useCredits";
import { CreditCostBadge } from "@/components/CreditGate";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import LaunchAdvisorResults, { type LaunchPlanData } from "@/components/launch-advisor/LaunchAdvisorResults";

const PRODUCT_TYPES = [
  "Ebook", "Mini Course", "Full Course", "Template Pack", "Swipe File",
  "Coaching Package", "Newsletter", "Workshop", "Challenge", "Other",
];

const AUDIENCE_SIZES = [
  { value: "under-500", label: "Under 500" },
  { value: "500-1k", label: "500 – 1K" },
  { value: "1k-5k", label: "1K – 5K" },
  { value: "5k-10k", label: "5K – 10K" },
  { value: "10k-50k", label: "10K – 50K" },
  { value: "50k-plus", label: "50K+" },
];

const SITUATION_OPTIONS = [
  { id: "email-list", label: "I have an email list" },
  { id: "social-following", label: "I have a social media following" },
  { id: "sold-before", label: "I've sold a product before" },
  { id: "lead-magnet", label: "I have a lead magnet" },
  { id: "sales-page", label: "I have a sales page ready" },
  { id: "first-product", label: "This is my first product ever" },
];

const PLATFORM_OPTIONS = [
  { id: "tiktok", label: "TikTok" },
  { id: "youtube", label: "YouTube" },
  { id: "instagram", label: "Instagram" },
  { id: "twitter", label: "Twitter / X" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "email", label: "Email" },
];

const LaunchAdvisor = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [productTitle, setProductTitle] = useState("");
  const [productType, setProductType] = useState("");
  const [audienceSize, setAudienceSize] = useState("");
  const [situation, setSituation] = useState<string[]>([]);
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [result, setResult] = useState<LaunchPlanData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [importProducts, setImportProducts] = useState<any[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const { consume } = useCredits();

  // Pre-fill from URL params (from Monetisation AI or Sales Page Builder)
  useEffect(() => {
    const title = searchParams.get("title");
    const type = searchParams.get("type");
    if (title) setProductTitle(title);
    if (type) setProductType(type);
  }, [searchParams]);

  const toggleCheckbox = (list: string[], setList: (v: string[]) => void, id: string) => {
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const loadImportProducts = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("monetization_products")
      .select("id, title, product_type, pricing, why_it_fits, project_id, monetization_projects(niche, audience_type)")
      .order("created_at", { ascending: false })
      .limit(20);
    setImportProducts(data || []);
    setImportOpen(true);
  };

  const handleImport = (product: any) => {
    setProductTitle(product.title || "");
    setProductType(product.product_type || "");
    setImportOpen(false);
    toast.success("Product details imported!");
  };

  const handleGenerate = async () => {
    if (!productTitle.trim()) { toast.error("Please enter your product title"); return; }
    if (platforms.length === 0) { toast.error("Select at least one platform"); return; }
    const success = await consume("launch-advisor", "Launch Advisor generation");
    if (!success) return;
    setIsGenerating(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("monetization-ai", {
        body: {
          type: "launch-advisor",
          params: {
            productTitle: productTitle.trim(),
            productType,
            audienceSize,
            situation: situation.map((id) => SITUATION_OPTIONS.find((s) => s.id === id)?.label).filter(Boolean),
            platforms: platforms.map((id) => PLATFORM_OPTIONS.find((p) => p.id === id)?.label).filter(Boolean),
          },
        },
      });
      if (error) throw error;
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      setResult(parsed);
      toast.success("Launch plan generated!");
    } catch (err: any) {
      console.error("Launch Advisor error:", err);
      toast.error(err.message || "Failed to generate. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-destructive/10 border border-destructive/20 flex items-center justify-center">
          <Rocket className="w-7 h-7 text-destructive" />
        </div>
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">Launch Advisor</h1>
          <p className="text-muted-foreground">Get a personalised 7-day launch plan for your digital product</p>
        </div>
        <CreditCostBadge action="launch-advisor" className="ml-auto" />
      </div>

      {/* Form */}
      <Card className="bg-card/50 border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Launch Details
            </CardTitle>
            <Dialog open={importOpen} onOpenChange={setImportOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2" onClick={loadImportProducts}>
                  <Import className="w-4 h-4" />
                  Import from Monetisation AI
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Import Product Details</DialogTitle>
                </DialogHeader>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {importProducts.length === 0 && (
                    <p className="text-sm text-muted-foreground py-4 text-center">No products found. Create one in Monetisation AI first.</p>
                  )}
                  {importProducts.map((p) => (
                    <button key={p.id} onClick={() => handleImport(p)} className="w-full text-left p-3 rounded-lg border border-border/50 hover:border-primary/50 hover:bg-primary/5 transition-colors">
                      <p className="font-medium text-sm text-foreground">{p.title}</p>
                      <p className="text-xs text-muted-foreground">{p.product_type}</p>
                    </button>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Product Title */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Product Title *</label>
            <Input placeholder="e.g. The Content Creator's Toolkit" value={productTitle} onChange={(e) => setProductTitle(e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Product Type Dropdown */}
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Product Type</label>
              <Select value={productType} onValueChange={setProductType}>
                <SelectTrigger><SelectValue placeholder="Select product type" /></SelectTrigger>
                <SelectContent>
                  {PRODUCT_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Audience Size Dropdown */}
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Audience Size</label>
              <Select value={audienceSize} onValueChange={setAudienceSize}>
                <SelectTrigger><SelectValue placeholder="Select size" /></SelectTrigger>
                <SelectContent>
                  {AUDIENCE_SIZES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Current Situation Checkboxes */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Your Current Situation</label>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
              {SITUATION_OPTIONS.map((opt) => (
                <label key={opt.id} className="flex items-center gap-2.5 cursor-pointer group">
                  <Checkbox
                    checked={situation.includes(opt.id)}
                    onCheckedChange={() => toggleCheckbox(situation, setSituation, opt.id)}
                  />
                  <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Platform Multi-select */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Active Platforms *</label>
            <p className="text-xs text-muted-foreground mb-2">Your launch plan will be tailored to these platforms</p>
            <div className="flex flex-wrap gap-2">
              {PLATFORM_OPTIONS.map((opt) => {
                const active = platforms.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => toggleCheckbox(platforms, setPlatforms, opt.id)}
                    className={`px-3.5 py-1.5 rounded-lg text-sm border transition-colors ${
                      active
                        ? "border-primary bg-primary/10 text-primary font-medium"
                        : "border-border/50 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <Button variant="hero" size="lg" className="w-full" onClick={handleGenerate} disabled={isGenerating || !productTitle.trim() || platforms.length === 0}>
            {isGenerating ? (
              <><Loader2 className="w-5 h-5 animate-spin mr-2" />Generating…</>
            ) : (
              <><Sparkles className="w-5 h-5 mr-2" />Generate Launch Plan (2 Credits)</>
            )}
          </Button>
          {isGenerating && <AIGenerationLoader tool="launch-plan" />}
        </CardContent>
      </Card>

      {/* Results */}
      {result && <LaunchAdvisorResults data={result} />}
    </div>
  );
};

export default LaunchAdvisor;
